import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { readStoredSession, resolveSession, wantsFreshLogin } from './session';
import { fetchCringeState, startCringe, submitCringe, linkCringeReport } from './api';
import { sendReport, extractReportIdFromResponse } from '../../functions/sendReport';

const errMsg = (e) => e?.response?.data?.message || e?.message || 'Something went wrong';

// The reports API takes "HH:MM:SS"; the game saves "MM:SS" (question-insert.php sent "00:" + timer).
export const reportTime = (timer) => (String(timer).split(':').length === 2 ? `00:${timer}` : String(timer));

// Log the player in (otp / demo / saved session) — see session.js.
export const loginCringe = createAsyncThunk('cringe/login', async (_, { rejectWithValue }) => {
  try {
    return await resolveSession();
  } catch (e) {
    return rejectWithValue(errMsg(e));
  }
});

// Where is this player up to? (null until stage 1 has been opened.)
export const loadCringeState = createAsyncThunk('cringe/load', async (_, { getState, rejectWithValue }) => {
  try {
    return (await fetchCringeState(getState().cringe.session)).state;
  } catch (e) {
    return rejectWithValue(errMsg(e));
  }
});

// Push points/time to the ExtraMilePlay reports API from the browser (add on the first call, update after)
// and store the returned report id on the player's `stages` row (stage1.php's successResponse()).
// Calls are queued so two of them can never both "add" a report for the same player.
let reportQueue = Promise.resolve();
export const syncReport = createAsyncThunk('cringe/syncReport', (snapshot, { getState, dispatch }) => {
  const run = async () => {
    const { session, reportId } = getState().cringe;
    if (!session || ['demobypass', 'DEMO'].includes(session.role)) return null;
    const response = await sendReport({
      sessionId: session.sessionId,
      organizationId: session.organizationId,
      userId: session.userId,
      role: session.role,
      token: session.token,
      gameId: session.gameId,
      name: session.name || '',
      points: snapshot.points,
      time: snapshot.time,
      reportId: reportId || undefined,
    });
    const id = extractReportIdFromResponse(response);
    if (id && id !== reportId) {
      dispatch(reportLinked(id));
      await linkCringeReport(session, id);
    }
    return id || reportId;
  };
  const p = reportQueue.then(run, run);
  reportQueue = p.catch(() => {});
  return p;
});

// Opening stage 1 creates the player's `stages` row (idempotent on the server) and their report (0 points).
export const startCringeGame = createAsyncThunk('cringe/start', async (_, { getState, dispatch, rejectWithValue }) => {
  try {
    const { state } = await startCringe(getState().cringe.session);
    if (!state.reportId) dispatch(syncReport({ points: 0, time: '00:00:00' }));
    return state;
  } catch (e) {
    return rejectWithValue(errMsg(e));
  }
});

// question-insert.php: save one score. Network hiccups are retried; a 4xx is a real answer, not retried.
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export const submitQuestion = createAsyncThunk(
  'cringe/submit',
  async ({ question, score, timer }, { getState, dispatch, rejectWithValue }) => {
    const session = getState().cringe.session;
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await submitCringe(session, question, score, timer);
        if (res.success) {
          dispatch(syncReport({ points: res.state.totalScore, time: reportTime(timer) }));
          return res.state;
        }
        if (res.returnData === 'ignored') return res.state; // already recorded (e.g. an earlier retry got through)
        return rejectWithValue(res.error || res.message || 'Score not accepted');
      } catch (e) {
        if (e?.response && e.response.status < 500) return rejectWithValue(errMsg(e));
        lastErr = e;
        await sleep(400 * (attempt + 1));
      }
    }
    return rejectWithValue(errMsg(lastErr));
  }
);

const slice = createSlice({
  name: 'cringe',
  initialState: {
    session: wantsFreshLogin() ? null : readStoredSession(),
    authStatus: 'idle', // idle -> loading -> succeeded | failed
    authError: null,
    starting: false,
    startTried: false, // start is attempted once per login, never in a loop
    game: null, // server state of the player's `stages` row, null until stage 1 is opened
    loaded: false,
    reportId: null,
    error: null,
  },
  reducers: {
    reportLinked: (s, a) => {
      s.reportId = a.payload;
    },
  },
  extraReducers: (b) => {
    b.addCase(loginCringe.pending, (s) => {
      s.authStatus = 'loading';
    })
      .addCase(loginCringe.fulfilled, (s, a) => {
        s.session = a.payload;
        s.authStatus = 'succeeded';
        s.authError = null;
        s.game = null; // progress belongs to whoever just logged in
        s.loaded = false;
        s.startTried = false;
        s.reportId = null;
      })
      .addCase(loginCringe.rejected, (s, a) => {
        s.authStatus = 'failed';
        s.authError = a.payload;
      })
      .addCase(loadCringeState.fulfilled, (s, a) => {
        s.game = a.payload;
        if (a.payload?.reportId) s.reportId = a.payload.reportId;
        s.loaded = true;
        s.error = null;
      })
      .addCase(loadCringeState.rejected, (s, a) => {
        s.loaded = true;
        s.error = a.payload;
      })
      .addCase(startCringeGame.pending, (s) => {
        s.starting = true;
        s.startTried = true;
      })
      .addCase(startCringeGame.fulfilled, (s, a) => {
        s.starting = false;
        s.game = a.payload;
        if (a.payload?.reportId) s.reportId = a.payload.reportId;
        s.error = null;
      })
      .addCase(startCringeGame.rejected, (s, a) => {
        s.starting = false;
        s.error = a.payload;
      })
      .addCase(submitQuestion.fulfilled, (s, a) => {
        if (a.payload) s.game = a.payload;
      })
      .addCase(submitQuestion.rejected, (s, a) => {
        s.error = a.payload;
      })
      .addCase(syncReport.rejected, (s, a) => {
        console.error('cringe report failed:', a.error?.message);
      });
  },
});

export const { reportLinked } = slice.actions;
export default slice.reducer;
